// Product videos in Add product (1023): up to 3 uploads (MP4, WebM or MOV, ≤ 50 MB each) next to the
// photos. Each upload shows its own progress and can be cancelled; a finished video shows its poster
// (a frame captured in the browser), its length and a play button that opens a preview.
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "@ady/i18n";
import { cx, Icon, Popup, useToast } from "@ady/ui";
import { ApiError } from "../api/client";
import { UploadRejected } from "../media/upload";
import { MAX_VIDEO_BYTES, uploadVideo, videoType, type VideoPhase } from "../media/uploadVideo";
import { catalogKeys } from "./api";
import { catalogError, useStoreIds } from "./ui";

export const MAX_VIDEOS = 3;

export interface VideoDraft {
  asset_id: string;
  poster_asset_id: string | null;
  poster_path: string | null;
  duration_ms: number | null;
  file_path: string;
  name: string;
}

interface Pending {
  key: string;
  name: string;
  phase: VideoPhase;
  percent: number;
  abort: AbortController;
}

/** m:ss (or h:mm:ss) for a length in ms. */
export function clock(ms: number): string {
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

export function ProductVideos({
  videos,
  onChange,
  onBusy,
  max = MAX_VIDEOS,
  help,
}: {
  videos: VideoDraft[];
  onChange: (v: VideoDraft[]) => void;
  /** How many uploads are still running (the form waits for them). */
  onBusy: (n: number) => void;
  /** At most this many videos (a collection cover takes one). */
  max?: number;
  /** The line under the tiles; null hides it. */
  help?: string | null;
}) {
  const { orgId, storeId } = useStoreIds();
  const toast = useToast();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<Pending[]>([]);
  const [playing, setPlaying] = useState<VideoDraft | null>(null);
  const latest = useRef(videos);
  useEffect(() => {
    latest.current = videos;
  }, [videos]);
  useEffect(() => onBusy(pending.length), [pending.length, onBusy]);
  // Leaving the form cancels what is still uploading.
  const live = useRef(pending);
  useEffect(() => {
    live.current = pending;
  }, [pending]);
  useEffect(() => () => live.current.forEach((p) => p.abort.abort()), []);

  const room = max - videos.length - pending.length;
  const patch = (key: string, next: Partial<Pending>) =>
    setPending((ps) => ps.map((p) => (p.key === key ? { ...p, ...next } : p)));

  const fail = (name: string, err: unknown) => {
    if (err instanceof DOMException && err.name === "AbortError") return;
    const key =
      err instanceof UploadRejected
        ? err.reason === "type"
          ? "catalog.add.videoType"
          : "catalog.add.videoTooLarge"
        : "catalog.add.videoFailed";
    toast.show({
      tone: "error",
      title: t(key, { name }),
      ...(err instanceof ApiError ? { body: catalogError(err) } : {}),
    });
  };

  const onFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.target.files ?? [])];
    e.target.value = "";
    if (!files.length) return;
    if (files.length > room) toast.show({ tone: "warning", title: t("catalog.add.videoMax") });
    for (const f of files.slice(0, Math.max(0, room))) {
      // Checked here too, so a wrong file never shows a progress tile.
      if (!videoType(f)) {
        fail(f.name, new UploadRejected("type"));
        continue;
      }
      if (f.size > MAX_VIDEO_BYTES) {
        fail(f.name, new UploadRejected("size"));
        continue;
      }
      const key = crypto.randomUUID();
      const abort = new AbortController();
      setPending((ps) => [...ps, { key, name: f.name, phase: "preparing", percent: 0, abort }]);
      void uploadVideo(
        orgId,
        storeId,
        f,
        (phase, percent) => patch(key, { phase, percent }),
        abort.signal,
      )
        .then(({ asset, poster, durationMs }) => {
          latest.current = [
            ...latest.current,
            {
              asset_id: asset.id,
              poster_asset_id: poster?.id ?? null,
              poster_path: poster?.preview_path ?? null,
              duration_ms: durationMs,
              file_path: `/api/v1/orgs/${orgId}/media/${asset.id}/file/master`,
              name: f.name,
            },
          ];
          onChange(latest.current);
          void qc.invalidateQueries({ queryKey: catalogKeys.media(orgId) });
        })
        .catch((err: unknown) => fail(f.name, err))
        .finally(() => setPending((ps) => ps.filter((p) => p.key !== key)));
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <ul className="flex flex-wrap gap-2" aria-label={t("catalog.add.secVideos")}>
        {videos.map((v) => (
          <li
            key={v.asset_id}
            className="relative h-20 w-32 overflow-hidden rounded-sm border border-card bg-sunken"
          >
            <button
              type="button"
              onClick={() => setPlaying(v)}
              title={t("catalog.add.videoPlay", { name: v.name })}
              className="group flex size-full items-center justify-center"
            >
              {v.poster_path ? (
                <img
                  src={v.poster_path}
                  alt=""
                  className="absolute inset-0 size-full object-cover"
                />
              ) : (
                <span className="absolute inset-0 flex items-end p-1 text-2xs text-fg-tertiary">
                  {t("catalog.add.videoNoPoster")}
                </span>
              )}
              <span className="relative flex size-8 items-center justify-center rounded-full bg-surface/90 text-link shadow-1 transition-transform group-hover:scale-110">
                <Icon name="play" />
              </span>
              <span className="ady-sr-only">{t("catalog.add.videoPlay", { name: v.name })}</span>
            </button>
            {v.duration_ms !== null ? (
              <span className="ady-ledger pointer-events-none absolute start-1 bottom-1 rounded-xs bg-surface/90 px-1 text-2xs font-semibold">
                {clock(v.duration_ms)}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => onChange(videos.filter((x) => x.asset_id !== v.asset_id))}
              title={t("catalog.add.videoRemove")}
              className="absolute end-1 top-1 flex size-5 items-center justify-center rounded-full bg-surface/90 text-fg-secondary hover:text-danger-fg"
            >
              <Icon name="x" />
              <span className="ady-sr-only">{t("catalog.add.videoRemove")}</span>
            </button>
          </li>
        ))}
        {pending.map((p) => (
          <li
            key={p.key}
            className="relative flex h-20 w-32 flex-col justify-end gap-1 overflow-hidden rounded-sm border border-card bg-sunken p-1.5"
            aria-busy="true"
          >
            <span className="truncate text-2xs text-fg-secondary" title={p.name}>
              {p.name}
            </span>
            <span
              className="flex items-center justify-between text-2xs font-semibold"
              role="status"
            >
              {p.phase === "preparing"
                ? t("catalog.add.videoPreparing")
                : p.phase === "finishing"
                  ? t("catalog.add.videoFinishing")
                  : t("catalog.add.videoUploading", { percent: p.percent })}
            </span>
            <span className="h-1 overflow-hidden rounded-full bg-control" aria-hidden>
              <span
                className={cx(
                  "ady-bg-brand block h-full rounded-full transition-[width]",
                  p.phase === "preparing" && "animate-pulse",
                )}
                style={{ width: `${p.phase === "preparing" ? 8 : Math.max(4, p.percent)}%` }}
              />
            </span>
            <button
              type="button"
              onClick={() => p.abort.abort()}
              title={t("catalog.add.videoCancel")}
              className="absolute end-1 top-1 flex size-5 items-center justify-center rounded-full bg-surface/90 text-fg-secondary hover:text-danger-fg"
            >
              <Icon name="x" />
              <span className="ady-sr-only">{t("catalog.add.videoCancel")}</span>
            </button>
          </li>
        ))}
        {room > 0 ? (
          <li>
            <button
              type="button"
              onClick={() => input.current?.click()}
              className="flex h-20 w-32 flex-col items-center justify-center gap-1 rounded-sm border border-dashed border-control bg-surface text-xs text-fg-secondary hover:border-link hover:bg-selected hover:text-link"
              data-testid="catalog-add-video"
            >
              <Icon name="video" />
              {t("catalog.add.videoUploadTile")}
            </button>
          </li>
        ) : null}
      </ul>
      {help === null ? null : (
        <p className="text-xs text-fg-tertiary">{help ?? t("catalog.add.videosHelp")}</p>
      )}
      <input
        ref={input}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,.mp4,.m4v,.webm,.mov"
        multiple={max > 1}
        hidden
        onChange={onFiles}
      />
      <Popup
        open={playing !== null}
        onClose={() => setPlaying(null)}
        size="md"
        label={t("catalog.add.videoPreview")}
      >
        {playing ? (
          <div className="flex flex-col gap-2">
            <header className="flex items-center gap-2">
              <h3 className="min-w-0 flex-1 truncate text-sm font-semibold">{playing.name}</h3>
              <button
                type="button"
                onClick={() => setPlaying(null)}
                title={t("catalog.rich.cancel")}
                className="flex size-7 items-center justify-center rounded-xs text-fg-secondary hover:bg-sunken"
              >
                <Icon name="x" />
                <span className="ady-sr-only">{t("catalog.rich.cancel")}</span>
              </button>
            </header>
            <video
              src={playing.file_path}
              poster={playing.poster_path ?? undefined}
              controls
              autoPlay
              playsInline
              className="max-h-[70vh] w-full rounded-sm bg-sunken"
            />
          </div>
        ) : null}
      </Popup>
    </div>
  );
}
